/*!
 * CHIEF - Cybersecurity Learning Platform (PROPRIETARY SOFTWARE)
 * Header Component
 * 
 * Copyright (c) 2025 Srivatsan Sk. All rights reserved.
 * 
 * PROPRIETARY LICENSE NOTICE:
 * This software is the exclusive proprietary property of Srivatsan Sk.
 * Licensed to client under strict commercial license agreement.
 * Unauthorized use, copying, modification, distribution, or removal
 * of copyright notices is strictly prohibited.
 * 
 * Original Author: Srivatsan Sk
 * License: Proprietary/Commercial
 * Status: Confidential
 * 
 * WARNING: This code contains proprietary business logic and algorithms.
 * Reverse engineering, decompilation, or derivative works are prohibited.
 */

// src/components/Layout/Header.js
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom'; 
import { FiBell, FiLogOut, FiUser } from 'react-icons/fi'; 
import api from '../utils/apiClient.js';
import { useNotifications } from '../contexts/NotificationContext';

// Styled Components
const HeaderContainer = styled.header`
  height: 64px;
  background-color: ${({ theme }) => theme.colors.backgroundLight || '#121212'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider || '#2a2a2a'};
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  flex: 1;
  max-width: 400px;
  margin: 0 1rem;
  
  @media (max-width: 768px) {
    max-width: 200px;
  }
`;

const NavGroup = styled.div`
  display: flex;
  align-items: center;
`;

const UserMenu = styled.div`
  position: relative;
  margin-left: ${({ theme }) => theme.spacing?.[4] || '1rem'};
`;

const IconButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  color: white;
  border: none;
  margin-left: 0.5rem;
  cursor: pointer;
  position: relative;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  svg {
    font-size: 1.3rem;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background-color: #ef4444;
  color: white;
  font-size: 0.6rem;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #4ade80;
  color: #121212;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  cursor: pointer;
  margin-left: 0.75rem;
`;

// Dropdown Container - Basic styles for all dropdown menus
const DropdownContainer = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: ${props => props.offsetRight || '0'};
  width: ${props => props.width || '250px'};
  background-color: #1e1e1e;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 50;
  overflow: hidden;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  border: 1px solid #333;
`;

// Notifications specific styles
const NotificationsDropdown = styled(DropdownContainer)`
  width: 300px;
`;

const NotificationsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #333;
`;

const NotificationsTitle = styled.h3`
  margin: 0;
  font-size: 0.9rem;
  color: white;
  font-weight: 600;
`;

const NotificationsList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const NotificationItem = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #333;
  background-color: ${({ is_read }) => (is_read ? 'transparent' : 'rgba(74, 222, 128, 0.05)')};
  cursor: pointer;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
  &::after {
      content: "${props => props.is_read ? 'Click to view details' : 'Click to mark as read'}";
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      background-color: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 0.4rem 0.8rem;
      border-radius: 4px;
      font-size: 0.7rem;
      white-space: nowrap;
      z-index: 60;
      pointer-events: none;
    }
`;

const NotificationMessage = styled.div`
  color: #fff;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
`;

const NotificationTime = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
`;

const EmptyNotifications = styled.div`
  padding: 1rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
`;

// User Menu specific styles
const UserMenuDropdown = styled(DropdownContainer)`
  width: 220px;
`;

const UserInfo = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #333;
`;

const UserName = styled.h4`
  margin: 0;
  color: white;
  font-size: 0.95rem;
  font-weight: 500;
`;

const UserEmail = styled.p`
  margin: 0.25rem 0 0;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
`;

const MenuItemsContainer = styled.div`
  padding: 0.5rem 0;
`;

const MenuItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.6rem 1rem;
  color: white;
  text-decoration: none;
  font-size: 0.9rem;
  
  svg {
    margin-right: 0.75rem;
    font-size: 1rem;
  }
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.6rem 1rem;
  color: ${props => props.danger ? '#ef4444' : 'white'};
  background: none;
  border: none;
  text-align: left;
  font-size: 0.9rem;
  cursor: pointer;
  
  svg {
    margin-right: 0.75rem;
    font-size: 1rem;
  }
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const Header = () => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Use notification context
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    error: notificationsError,
    fetchNotifications,
    refreshNotificationsIfNeeded,
    markAsRead,
    markAllAsRead,
    clearError
  } = useNotifications();

  const navigate = useNavigate();
  const notificationsRef = useRef(null);
  const userMenuRef = useRef(null);
  const searchRef = useRef(null);

  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await api.users.getProfile();
        setCurrentUser(userData);
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Keep the default user data if error
      }
    };

    fetchUser();
  }, []);

  // Only refresh notifications when dropdown is manually opened
  useEffect(() => {
    if (showNotifications && currentUser) {
      // Only refresh notifications when user specifically opens the dropdown
      console.log('User opened notification dropdown, refreshing...');
      refreshNotificationsIfNeeded();
    }
  }, [showNotifications, currentUser]); // Remove refreshNotificationsIfNeeded from dependencies

  // Clear notification errors when dropdown is closed
  useEffect(() => {
    if (!showNotifications && notificationsError) {
      clearError();
    }
  }, [showNotifications, notificationsError]); // Remove clearError to prevent loops

  // Show recent notifications (both read and unread) in header dropdown, but limit to recent ones
  const recentNotifications = notifications.slice(0, 5); // Show only 5 most recent notifications
  const unreadNotifications = notifications.filter(notification => !notification.is_read);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle notifications dropdown
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    setShowUserMenu(false);
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      console.log('Marking all notifications as read...');
      const success = await markAllAsRead();
      if (success) {
        console.log('Successfully marked all notifications as read');
        // Keep dropdown open to show the updated state
        // setTimeout(() => setShowNotifications(false), 1500);
      } else {
        console.error('Failed to mark all notifications as read');
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Toggle user menu dropdown
  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
    setShowNotifications(false);
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read if not already read (with visual feedback)
      if (!notification.is_read) {
        console.log('Marking notification as read:', notification.id);
        const success = await markAsRead(notification.id);
        if (!success) {
          console.error('Failed to mark notification as read');
          return;
        }
      }

      // Handle navigation or action based on notification type
      if (notification.link_url) {
        navigate(notification.link_url);
        setShowNotifications(false); // Close dropdown when navigating
      }

      // Don't close the notifications panel immediately to show feedback
      // User can still see the notification updated state
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await api.auth.logout(refreshToken);
      }
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      // Still navigate to login even if API call fails
      navigate('/login');
    }
  };

  // Helper function to format the notification time
  const formatNotificationTime = (created_at, time_diff) => {
    if (time_diff) {
      // Convert to minutes, hours, or days
      if (time_diff < 60) {
        return `${Math.floor(time_diff)} seconds ago`;
      } else if (time_diff < 3600) {
        return `${Math.floor(time_diff / 60)} minutes ago`;
      } else if (time_diff < 86400) {
        return `${Math.floor(time_diff / 3600)} hours ago`;
      } else {
        return `${Math.floor(time_diff / 86400)} days ago`;
      }
    }

    // Fallback to created_at if time_diff is not available
    if (created_at) {
      const date = new Date(created_at);
      return date.toLocaleString();
    }

    return 'Unknown time';
  };

  return (
    <HeaderContainer>
      <SearchBar ref={searchRef}>
        {/* Search functionality removed for clarity */}
      </SearchBar>
      <NavGroup>
        {/* Notifications */}
        <UserMenu ref={notificationsRef}>
          <IconButton onClick={toggleNotifications}>
            <FiBell />
            {unreadCount > 0 && <NotificationBadge>{unreadCount}</NotificationBadge>}
          </IconButton>

          <NotificationsDropdown isOpen={showNotifications}>
            <NotificationsHeader>
              <NotificationsTitle>Notifications</NotificationsTitle>
              {unreadNotifications.length > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  style={{
                    fontSize: '12px',
                    color: '#3b82f6',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Mark all read
                </button>
              )}
            </NotificationsHeader>

            <NotificationsList>
              {notificationsLoading ? (
                <EmptyNotifications>Loading notifications...</EmptyNotifications>
              ) : notificationsError ? (
                <EmptyNotifications style={{ color: '#ff4444' }}>
                  {notificationsError}
                  <button
                    onClick={() => fetchNotifications()}
                    style={{
                      marginLeft: '8px',
                      fontSize: '12px',
                      color: '#3b82f6',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    Retry
                  </button>
                </EmptyNotifications>
                ) : recentNotifications.length > 0 ? (
                  recentNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    is_read={notification.is_read}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {notification.title && (
                      <div style={{
                        fontWeight: '600',
                        fontSize: '14px',
                        marginBottom: '4px',
                        color: '#fff'
                      }}>
                        {notification.title}
                      </div>
                    )}
                    <NotificationMessage>{notification.message}</NotificationMessage>
                    <NotificationTime>
                      {formatNotificationTime(notification.created_at, notification.time_diff)}
                    </NotificationTime>
                  </NotificationItem>
                ))
              ) : (
                <EmptyNotifications>
                  No new notifications
                  <div style={{ fontSize: '12px', marginTop: '4px', color: '#888' }}>
                    You're all caught up! New notifications will appear here.
                  </div>
                </EmptyNotifications>
              )}
              
              {/* View All Notifications Link */}
              {notifications.length > 0 && (
                <div style={{
                  padding: '0.75rem 1rem',
                  borderTop: '1px solid #333',
                  textAlign: 'center'
                }}>
                  <button
                    onClick={() => {
                      navigate('/notifications');
                      setShowNotifications(false);
                    }}
                    style={{
                      fontSize: '13px',
                      color: '#3b82f6',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontWeight: '500'
                    }}
                  >
                    View All Notifications
                  </button>
                </div>
              )}
            </NotificationsList>
          </NotificationsDropdown>
        </UserMenu>

        {/* User Menu */}
        <UserMenu ref={userMenuRef}>
          <UserAvatar onClick={toggleUserMenu}>
            {currentUser?.name?.charAt(0) || 'U'}
          </UserAvatar>

          <UserMenuDropdown isOpen={showUserMenu}>
            <UserInfo>
              <UserName>{currentUser?.name || 'User'}</UserName>
              <UserEmail>{currentUser?.email || 'user@thecyberhub.in'}</UserEmail>
            </UserInfo>

            <MenuItemsContainer>
              <MenuItem to="/profile">
                <FiUser />
                Profile
              </MenuItem>

              <MenuButton danger onClick={handleLogout}>
                <FiLogOut />
                Logout
              </MenuButton>
            </MenuItemsContainer>
          </UserMenuDropdown>
        </UserMenu>
      </NavGroup>
    </HeaderContainer>
  );
};

export default Header;